import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Card,
  Typography,
  Button,
  Form,
  Space,
  Skeleton,
  Spin,
  message,
  Select
} from 'antd'
import { ArrowLeft, ArrowRight, Check } from 'lucide-react'
import { Link as RouterLink } from 'react-router-dom'
import { useCompanyStore } from '../../../store/company/companyStore'
import { useApi, XeroConnection } from '../../../services/ApiContext'
import { PaidBlocker } from '../PaidBlocker'

const { Title, Text } = Typography

export const XeroSettings: React.FC = () => {
  const singleConnectionAttempted = useRef<boolean>(false)
  const [connected, setConnected] = React.useState(false)
  const [reconnecting, setReconnecting] = useState(false)
  const user = useCompanyStore((state) => state.user)
  const company = useCompanyStore((state) => state.company)
  const api = useApi()
  const clientId = import.meta.env.VITE_XERO_CLIENT_ID
  const redirectUri = import.meta.env.VITE_XERO_REDIRECT_URI
  const scopes = [
    'accounting.attachments',
    'accounting.contacts',
    'accounting.transactions',
    'offline_access',
    'accounting.settings.read'
  ]
  const patchCompany = useCompanyStore((state) => state.patchCompany)
  const [connectingXero, setConnectingXero] = useState(false)
  const [checkingConnection, setCheckingConnection] = useState(false)
  const [xeroAccounts, setXeroAccounts] = useState<
    Array<{
      value: string
      label: string
    }>
  >([])
  const [xeroConnection, setXeroConnection] = useState<XeroConnection | null>(
    null
  )
  const [loadingXeroAccounts, setLoadingXeroAccounts] = useState(false)
  const connectionUrl = `https://login.xero.com/identity/connect/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scopes.join(' ')}&state=${user?._id}`

  // get code from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    const state = urlParams.get('state')
    const scopes = urlParams.get('scope')
    if (code && state === user?._id && scopes) {
      connectXero(code, scopes)
    } else if (user && !user.isDemo) {
      getConnectionStatus()
    }
  }, [user])

  const connectXero = async (code: string, scopes: string) => {
    try {
      if (singleConnectionAttempted.current) return
      singleConnectionAttempted.current = true
      setConnectingXero(true)
      await api.connectXero(code, scopes.split(' '))
      setConnectingXero(false)
      await getConnectionStatus()
      patchCompany({ isXeroConnected: true })
      getXeroAccounts()
      const oldXeroAccountCode = localStorage.getItem('oldXeroAccountCode')
      if(oldXeroAccountCode) {
        await setXeroAccount(oldXeroAccountCode)
        const xeroConnectionCopy = { ...xeroConnection } as XeroConnection
        xeroConnectionCopy.xeroExpensesAccountId = oldXeroAccountCode
        setXeroConnection(xeroConnectionCopy)
        localStorage.removeItem('oldXeroAccountCode')
      }
    } catch (error) {
      console.error(error)
      setConnectingXero(false)
      message.error('Failed to connect to Xero, please try again.')
    }
    singleConnectionAttempted.current = false
  }

  const getConnectionStatus = async () => {
    try {
      setCheckingConnection(true)
      const response = await api.getXeroConnection()
      if(response.data[0]?.xeroExpensesAccountId) {
        localStorage.setItem('oldXeroAccountCode', response.data[0].xeroExpensesAccountId)
      }
      setConnected(response.total > 0)
      if (response.total > 0 && company.isXeroConnected && !company.xeroNeedsReconnect) {
        setXeroConnection(response.data[0])
        getXeroAccounts()
      }
      setCheckingConnection(false)
      console.log(response)
    } catch (error) {
      console.error(error)
      message.error('Failed to load Xero status, please try again.')
    }
  }

  const disconnectXero = async () => {
    try {
      setCheckingConnection(true)
      await api.disconnectXero()
      patchCompany({ isXeroConnected: false })
      setConnected(false)
      setCheckingConnection(false)
    } catch (error) {
      console.error(error)
      message.error('Failed to disconnect from Xero, please try again.')
    }
  }

  const getXeroAccounts = async () => {
    setLoadingXeroAccounts(true)
    try {
      const response = await api.getXeroAccounts()
      setXeroAccounts(
        response.map((account) => ({
          value: account.code,
          label: account.name
        }))
      )
    } catch (error) {
      console.error(error)
      message.error('Failed to load Xero accounts, please try again.')
    } finally {
      setLoadingXeroAccounts(false)
    }
  }

  const setXeroAccount = async (accountCode: string) => {
    try {
      await api.setExpensesAccount(accountCode)
      message.success('Default account updated')

      if (xeroConnection) {
        setXeroConnection({
          ...xeroConnection,
          xeroExpensesAccountId: accountCode
        })
      }
    } catch (error) {
      console.error(error)
      message.error('Failed to update default account, please try again.')
    }
  }

  const reconnectXero = async () => {
    try {
      setReconnecting(true)
      await api.disconnectXero()
      patchCompany({ isXeroConnected: false })
      window.location.href = connectionUrl
    } catch (error) {
      console.error(error)
      setReconnecting(false)
      message.error('Failed to start reconnection, please try again.')
    }
  }

  const needsReconnect = connected && company.xeroNeedsReconnect

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start gap-2 mb-4">
        <RouterLink to="/app/settings/integrations">
          <Button
            size="small"
            type="text"
            icon={<ArrowLeft className="h-4 w-4" />}
          >
            Back to Integrations
          </Button>
        </RouterLink>
        <div>
          <Title level={4} className="!mb-0">
            Xero Integration
          </Title>
          <Text type="secondary">
            Sync your purchase orders and suppliers with Xero
          </Text>
        </div>
      </div>

      <Card>
        <div className="flex items-start justify-between mb-6">
          <Space>
            <img
              src="/xero_logo.svg"
              alt="Xero"
              className="w-12 h-12 rounded"
            />
            <div>
              <Text strong className="block">
                Xero
              </Text>
              <Text type="secondary">Accounting Ledger</Text>
            </div>
          </Space>
        </div>
        {checkingConnection && <Skeleton active />}
        {connectingXero && (
          <Spin tip={'Finishing setup...'}>
            <div className="w-full h-[200px]"></div>
          </Spin>
        )}

        <PaidBlocker action="enable Xero integration">
          {!checkingConnection && !connectingXero && user?.isDemo && (
            <div className="flex gap-4 items-center">
              <Text className="text-md font-semibold">
                Sign up to enable Xero integration
              </Text>
              <RouterLink to="/signup">
                <Button type="primary">
                  Sign up <ArrowRight className="h-4 w-4" />
                </Button>
              </RouterLink>
            </div>
          )}

          {!connected &&
            !checkingConnection &&
            !connectingXero &&
            !user?.isDemo && (
              <div>
                <Button type="primary" href={connectionUrl}>
                  Connect to Xero
                </Button>
              </div>
            )}
          {connected && !checkingConnection && !connectingXero && needsReconnect && (
            <div className="space-y-4">
              <Alert
                type="warning"
                showIcon
                message="Reconnection required"
                description="Due to a Xero update, you need to reconnect your account to continue syncing."
              />
              <Button
                type="primary"
                loading={reconnecting}
                onClick={reconnectXero}
              >
                Reconnect to Xero
              </Button>
            </div>
          )}
          {connected && !checkingConnection && !connectingXero && !needsReconnect && (
            <Form layout="vertical">
              {/* Connected status */}
              <Form.Item>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="flex flex-row gap-2">
                      <Check className="h-6 w-6 text-green-500" />

                      <Text strong className="block">
                        Connected
                      </Text>
                    </div>
                    <Text type="secondary">
                      Your account is connected to Xero
                    </Text>
                  </div>
                </div>
              </Form.Item>

              <Title level={5} className="!mt-8 !mb-4">
                Features
              </Title>

              <Form.Item>
                <div className="flex justify-between items-center">
                  <div>
                    <Text strong className="block">
                      Send Purchase Orders
                    </Text>
                    <Text type="secondary">
                      Open a purchase order in SprintSpec to send it to Xero
                    </Text>
                  </div>
                </div>
              </Form.Item>
              <Form.Item label="Default Cost of Goods Sold Account">
                <Select
                  loading={loadingXeroAccounts}
                  value={xeroConnection?.xeroExpensesAccountId}
                  placeholder="Select a default account"
                  options={xeroAccounts}
                  onChange={setXeroAccount}
                />
                <p className="text-gray-400">
                  Purchase orders sent over to Xero will use this as their
                  default account code.
                </p>
              </Form.Item>

              {/* <Form.Item>
              <div className="flex justify-between items-center">
                <div>
                  <Text strong className="block">
                    Sync Invoices
                  </Text>
                  <Text type="secondary">Create draft invoices in Xero</Text>
                </div>
                <Switch defaultChecked />
              </div>
            </Form.Item> */}

              {/* <Form.Item>
              <div className="flex justify-between items-center">
                <div>
                  <Text strong className="block">
                    Sync Contacts
                  </Text>
                  <Text type="secondary">Import suppliers from Xero</Text>
                </div>
                <Switch defaultChecked />
              </div>
            </Form.Item> */}

              <Form.Item>
                <Space>
                  {/* <Button type="primary">Save Settings</Button> */}
                  <Button danger onClick={disconnectXero}>
                    Disconnect
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          )}
        </PaidBlocker>
      </Card>

      {/* <Card>
        <Title level={5}>Activity Log</Title>
        <Text type="secondary" className="block mb-4">
          Recent sync activity and errors
        </Text>
        {connected ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <Check className="h-4 w-4 text-green-500" />
              <Text>Successfully synced 5 purchase orders</Text>
              <Text type="secondary">2 minutes ago</Text>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Check className="h-4 w-4 text-green-500" />
              <Text>Updated supplier contacts</Text>
              <Text type="secondary">1 hour ago</Text>
            </div>
          </div>
        ) : (
          <Text type="secondary">Connect to Xero to view sync activity</Text>
        )}
      </Card> */}
    </div>
  )
}
